/**
 * InlineLicenseForm - Compact license form for embedding in modals
 *
 * Used when adding literature to require license confirmation upfront.
 * Shows URL-detected suggestions when available.
 */

import { useState, useEffect } from 'react';
import { Shield, CheckCircle, AlertTriangle, Info, ExternalLink, Loader2, BookOpen, X } from 'lucide-react';

// License types matching database enum (expanded for Norwegian types)
export type LicenseType =
  | 'unknown'
  | 'public_domain'
  | 'creative_commons'
  | 'proprietary'
  | 'educational'
  | 'open_access'
  | 'subscription'
  | 'purchased'
  | 'citation_right'
  | 'kopinor_agreement';

export type CCVariant =
  | 'CC0'
  | 'CC-BY'
  | 'CC-BY-SA'
  | 'CC-BY-NC'
  | 'CC-BY-NC-SA'
  | 'CC-BY-ND'
  | 'CC-BY-NC-ND';

export const LICENSE_TYPE_LABELS: Record<LicenseType, string> = {
  unknown: 'Ukjent',
  public_domain: 'Offentlig domene',
  creative_commons: 'Creative Commons',
  proprietary: 'Proprietær',
  educational: 'Utdanningslisens',
  open_access: 'Åpen tilgang',
  subscription: 'Abonnement',
  purchased: 'Kjøpt lisens',
  citation_right: 'Sitatrett',
  kopinor_agreement: 'Kopinor-avtale'
};

export const CC_VARIANT_LABELS: Record<CCVariant, string> = {
  CC0: 'CC0 (Public Domain)',
  'CC-BY': 'CC BY (Navngivelse)',
  'CC-BY-SA': 'CC BY-SA (Navngivelse-DelPåSammeVilkår)',
  'CC-BY-NC': 'CC BY-NC (Navngivelse-IkkeKommersiell)',
  'CC-BY-NC-SA': 'CC BY-NC-SA (Navngivelse-IkkeKommersiell-DelPåSammeVilkår)',
  'CC-BY-ND': 'CC BY-ND (Navngivelse-IngenBearbeidelser)',
  'CC-BY-NC-ND': 'CC BY-NC-ND (Navngivelse-IkkeKommersiell-IngenBearbeidelser)'
};

// Detailed descriptions for each license type
export const LICENSE_TYPE_DESCRIPTIONS: Record<LicenseType, { short: string; long: string; canShare: boolean; example: string }> = {
  unknown: {
    short: 'Lisenstype er ikke spesifisert. Du må velge en lisenstype.',
    long: 'Når lisenstypen er ukjent, må du undersøke hvilke rettigheter som gjelder for materialet før du bruker det. Kontakt rettighetshaver eller sjekk kildens nettside.',
    canShare: false,
    example: 'Materiale uten tydelig lisensangivelse'
  },
  public_domain: {
    short: 'Helt fritt tilgjengelig uten restriksjoner. Kan brukes, deles og endres av alle.',
    long: 'Offentlig domene betyr at opphavsretten har utløpt eller at rettighetshaver har frasagt seg alle rettigheter. Materialet kan brukes fritt til alle formål, inkludert kommersielle, uten kreditering.',
    canShare: true,
    example: 'Historiske dokumenter, lovtekster, offentlige utredninger (NOU)'
  },
  creative_commons: {
    short: 'Standardiserte lisenser som gir ulike nivåer av frihet. Velg CC-variant for detaljer.',
    long: 'Creative Commons er et sett med lisenser som lar skapere velge hvilke rettigheter de vil beholde. Varianten bestemmer om du må kreditere, kan tjene penger, kan endre, og om du må dele videre på samme vilkår.',
    canShare: true,
    example: 'NDLA-ressurser, Wikipedia, mange akademiske artikler'
  },
  open_access: {
    short: 'Gratis tilgjengelig for lesing og ofte gjenbruk. Kreditering vanligvis påkrevd.',
    long: 'Åpen tilgang (Open Access) betyr at innholdet er fritt tilgjengelig på internett uten betalingsmur. De fleste OA-publikasjoner krever kreditering, men tillater akademisk gjenbruk.',
    canShare: true,
    example: 'Vitenskapelige artikler i åpne tidsskrifter, DUO (UiO), NORA'
  },
  citation_right: {
    short: 'Norsk lov gir rett til å sitere mindre deler av verk med kreditering.',
    long: 'Sitatretten (Åndsverkloven §29) gir alle rett til å sitere fra offentliggjorte verk i samsvar med god skikk og i den utstrekning formålet betinger. Sitatet må være begrenset i omfang og kilde må oppgis.',
    canShare: true,
    example: 'Korte utdrag fra bøker, artikler eller nettsider i undervisningssammenheng'
  },
  kopinor_agreement: {
    short: 'Norsk avtale for kopiering i undervisning. Krever at mottaker også har Kopinor-avtale.',
    long: 'Kopinor-avtalen gir utdanningsinstitusjoner rett til å kopiere begrenset mengde fra opphavsrettsbeskyttet materiale. Avtalen dekker papirkopiering og digital kopiering innenfor visse rammer. Mottaker må ha egen Kopinor-avtale.',
    canShare: true,
    example: 'Bokkapitler, tidsskriftartikler kopiert for undervisning'
  },
  proprietary: {
    short: 'Alle rettigheter forbeholdt rettighetshaver. Kan ikke deles uten tillatelse.',
    long: 'Proprietær lisens betyr at rettighetshaver beholder full kontroll. Du kan ikke dele, kopiere eller endre materialet uten eksplisitt skriftlig tillatelse. Brudd kan medføre erstatningsansvar.',
    canShare: false,
    example: 'Kommersielle lærebøker, programvare, abonnementsinnhold'
  },
  educational: {
    short: 'Lisens begrenset til utdanningsformål. Ofte organisasjonsspesifikk.',
    long: 'Utdanningslisenser gir tilgang til materiale kun for undervisning og læring. De er ofte knyttet til en spesifikk institusjon og kan ikke overføres. Kommersielt bruk er vanligvis forbudt.',
    canShare: false,
    example: 'Campuslisenser, akademiske programvarelisenser'
  },
  subscription: {
    short: 'Tilgang så lenge abonnementet er aktivt. Kan ikke deles utenfor organisasjonen.',
    long: 'Abonnementslisenser gir tidsbegrenset tilgang mot betaling. Innholdet kan vanligvis ikke lastes ned permanent eller deles med andre som ikke har abonnement.',
    canShare: false,
    example: 'Digitale tidsskrifter, e-bokplattformer, strømmetjenester'
  },
  purchased: {
    short: 'Kjøpt tilgang til spesifikt innhold. Delingsrettigheter varierer.',
    long: 'Kjøpt lisens gir vanligvis personlig bruksrett. Videredeling kan være begrenset eller forbudt avhengig av avtalevilkår. Sjekk alltid lisensvilkårene ved kjøp.',
    canShare: false,
    example: 'E-bøker, enkeltstående programvare, digitale kurs'
  }
};

// CC variant descriptions
export const CC_VARIANT_DESCRIPTIONS: Record<CCVariant, { short: string; permissions: string[] }> = {
  CC0: {
    short: 'Ingen rettigheter forbeholdt. Tilsvarer offentlig domene.',
    permissions: ['Kan deles fritt', 'Kan endres fritt', 'Kan brukes kommersielt', 'Ingen kreditering påkrevd']
  },
  'CC-BY': {
    short: 'Kan brukes fritt så lenge du krediterer opphavspersonen.',
    permissions: ['Kan deles fritt', 'Kan endres fritt', 'Kan brukes kommersielt', 'Kreditering påkrevd']
  },
  'CC-BY-SA': {
    short: 'Som CC-BY, men endringer må deles på samme vilkår.',
    permissions: ['Kan deles fritt', 'Kan endres', 'Kan brukes kommersielt', 'Kreditering påkrevd', 'Må dele videre på samme vilkår']
  },
  'CC-BY-NC': {
    short: 'Kan brukes og endres, men ikke til kommersielle formål.',
    permissions: ['Kan deles fritt', 'Kan endres fritt', 'Kun ikke-kommersiell bruk', 'Kreditering påkrevd']
  },
  'CC-BY-NC-SA': {
    short: 'Ikke-kommersiell bruk med deling på samme vilkår.',
    permissions: ['Kan deles fritt', 'Kan endres', 'Kun ikke-kommersiell bruk', 'Kreditering påkrevd', 'Må dele videre på samme vilkår']
  },
  'CC-BY-ND': {
    short: 'Kan deles, men må beholdes uendret.',
    permissions: ['Kan deles fritt', 'Kan IKKE endres', 'Kan brukes kommersielt', 'Kreditering påkrevd']
  },
  'CC-BY-NC-ND': {
    short: 'Mest restriktive CC-lisens. Kun deling uendret, ikke-kommersielt.',
    permissions: ['Kan deles fritt', 'Kan IKKE endres', 'Kun ikke-kommersiell bruk', 'Kreditering påkrevd']
  }
};

// Open license types that allow sharing
const OPEN_LICENSE_TYPES: LicenseType[] = [
  'public_domain',
  'creative_commons',
  'citation_right',
  'open_access'
];

export interface LicenseFormData {
  license_type: LicenseType;
  cc_variant: CCVariant | null;
  allows_distribution: boolean;
  allows_modification: boolean;
  allows_commercial_use: boolean;
  requires_attribution: boolean;
  attribution_text: string;
  rights_holder: string;
  license_url: string;
  valid_until: string | null;
  is_perpetual: boolean;
  is_organization_specific: boolean;
  max_users: number | null;
  usage_notes: string;
}

export interface DetectedLicense {
  detected: boolean;
  suggested_license_type: LicenseType;
  suggested_cc_variant?: CCVariant | null;
  allows_distribution?: boolean;
  allows_modification?: boolean;
  allows_commercial_use?: boolean;
  requires_attribution?: boolean;
  attribution_template?: string;
  confidence: number;
  source_description?: string;
  verification_url?: string;
  matched_pattern?: string;
}

interface InlineLicenseFormProps {
  value: LicenseFormData;
  onChange: (data: LicenseFormData) => void;
  detectedLicense?: DetectedLicense | null;
  isDetecting?: boolean;
  confirmed: boolean;
  onConfirmChange: (confirmed: boolean) => void;
  compact?: boolean;
}

export const getDefaultLicenseFormData = (): LicenseFormData => ({
  license_type: 'unknown',
  cc_variant: null,
  allows_distribution: true,
  allows_modification: true,
  allows_commercial_use: true,
  requires_attribution: false,
  attribution_text: '',
  rights_holder: '',
  license_url: '',
  valid_until: null,
  is_perpetual: true,
  is_organization_specific: false,
  max_users: null,
  usage_notes: ''
});

export default function InlineLicenseForm({
  value,
  onChange,
  detectedLicense,
  isDetecting,
  confirmed,
  onConfirmChange,
  compact = false
}: InlineLicenseFormProps) {

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showLicenseGuide, setShowLicenseGuide] = useState(false);

  // Apply detected license when it changes
  useEffect(() => {
    if (detectedLicense?.detected && value.license_type === 'unknown') {
      onChange({
        ...value,
        license_type: detectedLicense.suggested_license_type,
        cc_variant: detectedLicense.suggested_cc_variant || null,
        allows_distribution: detectedLicense.allows_distribution ?? true,
        allows_modification: detectedLicense.allows_modification ?? true,
        allows_commercial_use: detectedLicense.allows_commercial_use ?? true,
        requires_attribution: detectedLicense.requires_attribution ?? false,
        attribution_text: detectedLicense.attribution_template || ''
      });
    }
  }, [detectedLicense]);

  const updateField = <K extends keyof LicenseFormData>(field: K, val: LicenseFormData[K]) => {
    const updated = { ...value, [field]: val };

    // Reset cc_variant if license type changes away from CC
    if (field === 'license_type' && val !== 'creative_commons') {
      updated.cc_variant = null;
    }

    // Auto-set permissions based on license type
    if (field === 'license_type') {
      if (val === 'public_domain') {
        updated.allows_distribution = true;
        updated.allows_modification = true;
        updated.allows_commercial_use = true;
        updated.requires_attribution = false;
      } else if (val === 'proprietary') {
        updated.allows_distribution = false;
        updated.allows_modification = false;
        updated.allows_commercial_use = false;
        updated.requires_attribution = true;
      }
    }

    // Auto-set permissions based on CC variant
    if (field === 'cc_variant') {
      const variant = val as CCVariant;
      updated.allows_distribution = true;
      updated.requires_attribution = variant !== 'CC0';

      if (variant?.includes('NC')) {
        updated.allows_commercial_use = false;
      } else {
        updated.allows_commercial_use = true;
      }

      if (variant?.includes('ND')) {
        updated.allows_modification = false;
      } else if (variant?.includes('SA')) {
        updated.allows_modification = true;
      } else {
        updated.allows_modification = true;
      }
    }

    onChange(updated);

    // Reset confirmation when license type changes
    if (field === 'license_type' && confirmed) {
      onConfirmChange(false);
    }
  };

  const isOpenLicense = OPEN_LICENSE_TYPES.includes(value.license_type);

  return (
    <div className={`bg-gray-50 rounded-lg p-4 border ${confirmed ? 'border-green-300' : 'border-gray-200'}`}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Shield className={`${confirmed ? 'text-green-600' : 'text-gray-500'}`} size={20} />
        <h4 className="font-medium text-gray-900">Lisensinformasjon</h4>
        {confirmed && (
          <span className="ml-auto flex items-center gap-1 text-green-600 text-sm">
            <CheckCircle size={16} />
            Bekreftet
          </span>
        )}
      </div>

      {/* Detection status */}
      {isDetecting && (
        <div className="flex items-center gap-2 text-blue-600 text-sm mb-3">
          <Loader2 size={16} className="animate-spin" />
          Detekterer lisens fra URL...
        </div>
      )}

      {/* Detected license suggestion */}
      {detectedLicense?.detected && !isDetecting && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
            <div className="text-sm">
              <p className="font-medium text-blue-800">
                Foreslått lisens: {LICENSE_TYPE_LABELS[detectedLicense.suggested_license_type]}
                {detectedLicense.suggested_cc_variant && ` (${detectedLicense.suggested_cc_variant})`}
              </p>
              {detectedLicense.source_description && (
                <p className="text-blue-700 mt-1">{detectedLicense.source_description}</p>
              )}
              <div className="flex items-center gap-2 mt-1">
                <span className="text-blue-600">
                  Konfidens: {detectedLicense.confidence}/5
                </span>
                {detectedLicense.verification_url && (
                  <a
                    href={detectedLicense.verification_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center gap-1"
                  >
                    Verifiser <ExternalLink size={12} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main form */}
      <div className="space-y-3">
        {/* License type */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Lisenstype <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setShowLicenseGuide(true)}
              className="text-xs text-kf-primary hover:underline flex items-center gap-1"
            >
              <BookOpen size={12} />
              Se alle lisenstyper
            </button>
          </div>
          <select
            value={value.license_type}
            onChange={(e) => updateField('license_type', e.target.value as LicenseType)}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-kf-primary focus:border-transparent"
          >
            {Object.entries(LICENSE_TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          {/* License type info box */}
          {value.license_type !== 'unknown' && (
            <div className={`mt-2 p-3 rounded-lg text-sm ${
              LICENSE_TYPE_DESCRIPTIONS[value.license_type].canShare
                ? 'bg-green-50 border border-green-200'
                : 'bg-amber-50 border border-amber-200'
            }`}>
              <div className="flex items-start gap-2">
                <Info size={16} className={`flex-shrink-0 mt-0.5 ${
                  LICENSE_TYPE_DESCRIPTIONS[value.license_type].canShare ? 'text-green-600' : 'text-amber-600'
                }`} />
                <div>
                  <p className={`font-medium ${
                    LICENSE_TYPE_DESCRIPTIONS[value.license_type].canShare ? 'text-green-800' : 'text-amber-800'
                  }`}>
                    {LICENSE_TYPE_DESCRIPTIONS[value.license_type].short}
                  </p>
                  <p className={`text-xs mt-1 ${
                    LICENSE_TYPE_DESCRIPTIONS[value.license_type].canShare ? 'text-green-700' : 'text-amber-700'
                  }`}>
                    Eksempel: {LICENSE_TYPE_DESCRIPTIONS[value.license_type].example}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CC Variant (only if CC license selected) */}
        {value.license_type === 'creative_commons' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CC-variant <span className="text-red-500">*</span>
            </label>
            <select
              value={value.cc_variant || ''}
              onChange={(e) => updateField('cc_variant', e.target.value as CCVariant)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-kf-primary focus:border-transparent"
            >
              <option value="">Velg CC-variant...</option>
              {Object.entries(CC_VARIANT_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>

            {/* CC variant info box */}
            {value.cc_variant && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <p className="font-medium text-blue-800 mb-2">
                  {CC_VARIANT_DESCRIPTIONS[value.cc_variant].short}
                </p>
                <div className="flex flex-wrap gap-1">
                  {CC_VARIANT_DESCRIPTIONS[value.cc_variant].permissions.map((perm, i) => (
                    <span
                      key={i}
                      className={`px-2 py-0.5 rounded text-xs ${
                        perm.includes('IKKE') || perm.includes('Kun')
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {perm}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Permissions summary */}
        {!compact && value.license_type !== 'unknown' && (
          <div className="flex flex-wrap gap-2">
            <span className={`px-2 py-1 rounded text-xs ${value.allows_distribution ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {value.allows_distribution ? 'Kan deles' : 'Kan ikke deles'}
            </span>
            <span className={`px-2 py-1 rounded text-xs ${value.allows_modification ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {value.allows_modification ? 'Kan endres' : 'Ingen bearbeidelse'}
            </span>
            {value.allows_commercial_use === false && (
              <span className="px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-700">
                Ikke-kommersiell
              </span>
            )}
            {value.requires_attribution && (
              <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">
                Kreditering påkrevd
              </span>
            )}
          </div>
        )}

        {/* Attribution text (if required) */}
        {value.requires_attribution && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Krediteringstekst
            </label>
            <input
              type="text"
              value={value.attribution_text}
              onChange={(e) => updateField('attribution_text', e.target.value)}
              placeholder="F.eks.: Kilde: NDLA"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-kf-primary focus:border-transparent"
            />
          </div>
        )}

        {/* Advanced options toggle */}
        {!compact && (
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-kf-primary hover:underline"
          >
            {showAdvanced ? 'Skjul avanserte alternativer' : 'Vis avanserte alternativer'}
          </button>
        )}

        {/* Advanced options */}
        {showAdvanced && !compact && (
          <div className="space-y-3 pt-2 border-t">
            {/* Rights holder */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rettighetshaver
              </label>
              <input
                type="text"
                value={value.rights_holder}
                onChange={(e) => updateField('rights_holder', e.target.value)}
                placeholder="Navn på rettighetshaver"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-kf-primary focus:border-transparent"
              />
            </div>

            {/* License URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lisens-URL
              </label>
              <input
                type="url"
                value={value.license_url}
                onChange={(e) => updateField('license_url', e.target.value)}
                placeholder="https://..."
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-kf-primary focus:border-transparent"
              />
            </div>

            {/* Validity */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={value.is_perpetual}
                  onChange={(e) => {
                    updateField('is_perpetual', e.target.checked);
                    if (e.target.checked) updateField('valid_until', null);
                  }}
                  className="rounded border-gray-300 text-kf-primary focus:ring-kf-primary"
                />
                <span className="text-sm text-gray-700">Evig lisens</span>
              </label>

              {!value.is_perpetual && (
                <div className="flex-1">
                  <label className="block text-sm text-gray-700 mb-1">Utløper</label>
                  <input
                    type="date"
                    value={value.valid_until || ''}
                    onChange={(e) => updateField('valid_until', e.target.value || null)}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                  />
                </div>
              )}
            </div>

            {/* Organization specific */}
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={value.is_organization_specific}
                onChange={(e) => updateField('is_organization_specific', e.target.checked)}
                className="rounded border-gray-300 text-kf-primary focus:ring-kf-primary"
              />
              <span className="text-sm text-gray-700">Lisensen er kun gyldig for min organisasjon</span>
            </label>

            {/* Max users */}
            {value.is_organization_specific && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maks antall brukere (valgfritt)
                </label>
                <input
                  type="number"
                  min={1}
                  value={value.max_users || ''}
                  onChange={(e) => updateField('max_users', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Ubegrenset"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                />
              </div>
            )}

            {/* Usage notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bruksnotater
              </label>
              <textarea
                value={value.usage_notes}
                onChange={(e) => updateField('usage_notes', e.target.value)}
                placeholder="Eventuelle begrensninger eller merknader..."
                rows={2}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-kf-primary focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Warning for unknown/closed licenses */}
        {!isOpenLicense && value.license_type !== 'unknown' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={16} />
              <p className="text-sm text-yellow-800">
                Denne lisenstypen ({LICENSE_TYPE_LABELS[value.license_type]}) kan ikke deles fritt med andre.
                Andre brukere må bekrefte egen tilgang for å bruke denne litteraturen.
              </p>
            </div>
          </div>
        )}

        {/* Confirmation checkbox */}
        <div className={`border rounded-lg p-3 ${confirmed ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'}`}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => onConfirmChange(e.target.checked)}
              disabled={value.license_type === 'unknown' || (value.license_type === 'creative_commons' && !value.cc_variant)}
              className="mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">
                Jeg bekrefter at lisensinformasjonen er korrekt
              </span>
              <p className="text-xs text-gray-500 mt-1">
                Ved å bekrefte tar du ansvar for at du har rett til å bruke denne litteraturen i studieplanen.
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* License Guide Modal */}
      {showLicenseGuide && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-kf-primary" />
                Guide til lisenstyper
              </h3>
              <button
                type="button"
                onClick={() => setShowLicenseGuide(false)}
                className="p-1 hover:bg-gray-200 rounded-full"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Open licenses section */}
              <div>
                <h4 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                  <CheckCircle size={16} />
                  Åpne lisenser (kan deles med andre)
                </h4>
                <div className="space-y-3">
                  {(Object.entries(LICENSE_TYPE_DESCRIPTIONS) as [LicenseType, typeof LICENSE_TYPE_DESCRIPTIONS[LicenseType]][])
                    .filter(([_, desc]) => desc.canShare)
                    .map(([type, desc]) => (
                      <div key={type} className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h5 className="font-medium text-gray-900">{LICENSE_TYPE_LABELS[type]}</h5>
                            <p className="text-sm text-gray-700 mt-1">{desc.long}</p>
                            <p className="text-xs text-gray-500 mt-2">
                              <span className="font-medium">Eksempel:</span> {desc.example}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              updateField('license_type', type);
                              setShowLicenseGuide(false);
                            }}
                            className="ml-2 px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 whitespace-nowrap"
                          >
                            Velg denne
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* CC variants section */}
              <div>
                <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                  <Info size={16} />
                  Creative Commons-varianter
                </h4>
                <div className="space-y-3">
                  {(Object.entries(CC_VARIANT_DESCRIPTIONS) as [CCVariant, typeof CC_VARIANT_DESCRIPTIONS[CCVariant]][])
                    .map(([variant, desc]) => (
                      <div key={variant} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h5 className="font-medium text-gray-900">{CC_VARIANT_LABELS[variant]}</h5>
                            <p className="text-sm text-gray-700 mt-1">{desc.short}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {desc.permissions.map((perm, i) => (
                                <span
                                  key={i}
                                  className={`px-2 py-0.5 rounded text-xs ${
                                    perm.includes('IKKE') || perm.includes('Kun')
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-green-100 text-green-700'
                                  }`}
                                >
                                  {perm}
                                </span>
                              ))}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              updateField('license_type', 'creative_commons');
                              updateField('cc_variant', variant);
                              setShowLicenseGuide(false);
                            }}
                            className="ml-2 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap"
                          >
                            Velg denne
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Closed licenses section */}
              <div>
                <h4 className="font-semibold text-amber-700 mb-2 flex items-center gap-2">
                  <AlertTriangle size={16} />
                  Lukkede lisenser (kan ikke deles fritt)
                </h4>
                <div className="space-y-3">
                  {(Object.entries(LICENSE_TYPE_DESCRIPTIONS) as [LicenseType, typeof LICENSE_TYPE_DESCRIPTIONS[LicenseType]][])
                    .filter(([type, desc]) => !desc.canShare && type !== 'unknown')
                    .map(([type, desc]) => (
                      <div key={type} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h5 className="font-medium text-gray-900">{LICENSE_TYPE_LABELS[type]}</h5>
                            <p className="text-sm text-gray-700 mt-1">{desc.long}</p>
                            <p className="text-xs text-gray-500 mt-2">
                              <span className="font-medium">Eksempel:</span> {desc.example}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              updateField('license_type', type);
                              setShowLicenseGuide(false);
                            }}
                            className="ml-2 px-3 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 whitespace-nowrap"
                          >
                            Velg denne
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Additional info */}
              <div className="bg-gray-100 rounded-lg p-4 mt-4">
                <h4 className="font-semibold text-gray-800 mb-2">Tips for riktig lisensvalg</h4>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• <strong>Usikker på lisensen?</strong> Sjekk kildens nettside for lisensmerking, ofte i bunnteksten eller på "Om oss"-sider.</li>
                  <li>• <strong>Offentlige kilder</strong> (regjeringen.no, lovdata.no) er ofte i offentlig domene eller har åpen tilgang.</li>
                  <li>• <strong>Undervisningsressurser</strong> som NDLA bruker vanligvis CC-lisenser - sjekk hvilken variant.</li>
                  <li>• <strong>Ved tvil</strong>, velg en mer restriktiv lisens eller kontakt rettighetshaver.</li>
                </ul>
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50">
              <button
                type="button"
                onClick={() => setShowLicenseGuide(false)}
                className="w-full py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
